<template>
    <x-page :title="title">
        <template #header-actions>
            <x-back-button :disabled="isLoading" :display-warning="false" :click-handler="handleBackButtonClick" />
        </template>

        <div class="mb-3">
            Total peserta: <strong>{{ displayedResources.length }}</strong>
            <small class="text-muted">(dari {{ resources.length }})</small>
        </div>

        <div v-if="isGraduated" class="alert alert-info">
            <i class="fa fa-info-circle"></i> Event form ini telah difinalisasi kelulusannya. Data tidak dapat diubah lagi.
        </div>

        <div class="row mb-2">
            <div class="col-12 col-md-6">
                <div class="attendance-filter-container" style="align-items: flex-start;">
                    <label class="d-block mb-1 mb-md-0">Pencarian:</label>
                    <input
                        type="text"
                        class="form-control"
                        v-model="searchQuery"
                        placeholder="Nama, email, atau kode..."
                        style="width: 100%;"
                    />
                </div>
            </div>
            <div class="col-12 col-md-6 text-right">
                <div class="attendance-filter-container">
                    <label class="d-block d-md-inline mr-md-2 mb-1 mb-md-0">Filter Kehadiran:</label>
                    <div class="attendance-filter-inner-container">
                        <input
                            type="number"
                            class="form-control attendance-filter-input"
                            v-model.number="attendanceFilterMin"
                            min="0"
                            max="100"
                            placeholder="Min"
                        />
                        <input
                            type="number"
                            class="form-control attendance-filter-input"
                            v-model.number="attendanceFilterMax"
                            min="0"
                            max="100"
                            placeholder="Max"
                        />
                        <button class="btn btn-sm attendance-filter-reset" @click="clearAttendanceFilter">Reset</button>
                    </div>
                </div>
            </div>
        </div>

        <div class="table-responsive">
            <table class="table table-condensed table-striped">
                <thead>
                    <tr>
                        <th v-if="!isGraduated">
                            <input type="checkbox" :checked="allResourcesSelected" @click="toggleAllResources" />
                        </th>
                        <th @click="toggleSort('name')" style="cursor: pointer; white-space: nowrap;">
                            Nama
                            <i v-if="sortKey === 'name'" :class="['fa', sortOrder === 1 ? 'fa-sort-alpha-asc' : 'fa-sort-alpha-desc']"></i>
                            <i v-else class="fa fa-sort text-muted"></i>
                        </th>
                        <th @click="toggleSort('email')" style="cursor: pointer; white-space: nowrap;">
                            Email
                            <i v-if="sortKey === 'email'" :class="['fa', sortOrder === 1 ? 'fa-sort-alpha-asc' : 'fa-sort-alpha-desc']"></i>
                            <i v-else class="fa fa-sort text-muted"></i>
                        </th>
                        <th @click="toggleSort('code')" style="cursor: pointer; white-space: nowrap;">
                            Kode
                            <i v-if="sortKey === 'code'" :class="['fa', sortOrder === 1 ? 'fa-sort-alpha-asc' : 'fa-sort-alpha-desc']"></i>
                            <i v-else class="fa fa-sort text-muted"></i>
                        </th>
                        <th @click="toggleSort('attendance_percent')" style="cursor: pointer; white-space: nowrap;">
                            Kehadiran
                            <i v-if="sortKey === 'attendance_percent'" :class="['fa', sortOrder === 1 ? 'fa-sort-numeric-asc' : 'fa-sort-numeric-desc']"></i>
                            <i v-else class="fa fa-sort text-muted"></i>
                        </th>
                        <th @click="toggleSort('graduation_status')" style="cursor: pointer; white-space: nowrap;">
                            Status
                            <i v-if="sortKey === 'graduation_status'" :class="['fa', sortOrder === 1 ? 'fa-sort-alpha-asc' : 'fa-sort-alpha-desc']"></i>
                            <i v-else class="fa fa-sort text-muted"></i>
                        </th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-show="!isLoading" v-for="resource in displayedResources" :key="resource.id">
                        <td v-if="!isGraduated">
                            <input
                                type="checkbox"
                                :checked="isResourceSelected(resource)"
                                @click="handleMultiCheck($event, (element) => toggleMultiResourceSelect(element))"
                                class="order-shift-checkbox"
                                :data-resource-id="resource.id"
                            />
                        </td>
                        <td>
                            <div>
                                <button
                                    type="button"
                                    class="btn btn-link"
                                    @click="showSubscriberDetailModal(resource)"
                                    style="padding: 0; border-width: 0"
                                    title="Klik untuk lihat detail"
                                >
                                    {{ resource.name }}
                                </button>
                            </div>
                            <div v-if="resource.member_account">
                                <x-label
                                    color="primary"
                                    :title="`Tersambung dengan akun ${resource.member_account.name}`"
                                >
                                    {{ resource.member_account.name }}
                                </x-label>
                            </div>
                            <div v-else class="text-warning">
                                <i class="fa fa-exclamation-triangle" />
                                Data kelulusan akan disimpan di Modul "Jemaat"
                            </div>
                        </td>
                        <td>
                            <span v-if="!(resource.member_account && resource.member_account.email)">
                                {{ resource.email }}
                            </span>
                        </td>
                        <td>
                            <code>{{ resource.code }}</code>
                        </td>
                        <td>{{ Number(resource.attendance_percent || 0).toFixed(2) }}%</td>
                        <td>
                            <x-input-graduation-status v-if="!isGraduated" v-model="resource.graduation_status" />
                            <span v-else>
                                <span :class="{'text-success': resource.graduation_status === 'Lulus', 'text-danger': resource.graduation_status === 'Tidak Lulus', 'text-muted': resource.graduation_status === 'Tanpa Status'}">
                                    <i :class="{'fa fa-check-circle': resource.graduation_status === 'Lulus', 'fa fa-times-circle': resource.graduation_status === 'Tidak Lulus', 'fa fa-minus-circle': resource.graduation_status === 'Tanpa Status'}"></i>
                                    {{ resource.graduation_status }}
                                </span>
                            </span>
                        </td>
                    </tr>
                    <tr v-if="!isLoading && resources.length <= 0">
                        <td :colspan="isGraduated ? 5 : 6" class="text-center">Tidak ada data.</td>
                    </tr>
                    <tr v-if="isLoading">
                        <td :colspan="isGraduated ? 5 : 6" class="text-center">
                            <i class="fa fa-spinner fa-spin" />
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>

        <!-- Subscriber detail modal -->
        <x-modal id="subscriber-detail-modal" :title="`Detail ${selectedResource ? selectedResource.name : ''}`">
            <div class="table-responsive" v-if="selectedResource">
                <table class="table table-condensed table-striped">
                    <tbody>
                        <tr>
                            <th>Nama</th>
                            <td>{{ selectedResource.name }}</td>
                        </tr>
                        <tr>
                            <th>Kode</th>
                            <td>
                                <code>{{ selectedResource.code }}</code>
                            </td>
                        </tr>
                        <tr>
                            <th>Email</th>
                            <td>{{ selectedResource.email }}</td>
                        </tr>
                        <tr v-if="selectedResource.dob">
                            <th>Tanggal Lahir</th>
                            <td>{{ selectedResource.dob | date }}</td>
                        </tr>
                        <tr v-if="selectedResource.phone">
                            <th>HP</th>
                            <td>{{ selectedResource.phone }}</td>
                        </tr>
                        <tr v-if="selectedResource.gender">
                            <th>Jenis Kelamin</th>
                            <td>{{ selectedResource.gender | gender }}</td>
                        </tr>
                        <tr v-if="selectedResource.address">
                            <th>Alamat</th>
                            <td>
                                <p v-html="$options.filters.nl2br(selectedResource.address)"></p>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
            <template #footer>
                <x-close-modal-button />
            </template>
        </x-modal>

        <!-- Bulk action modal trigger -->
        <button
            v-if="!isGraduated && selectedResources.length > 0"
            class="btn btn-lg btn-success"
            style="position: fixed; left: 50%; bottom: 10px; transform: translateX(-50%); border-radius: 50px"
            @click="showBulkActionModal"
        >
            Ubah Status {{ selectedResources.length }} Peserta
        </button>

        <!-- Bulk action modal -->
        <x-modal id="bulk-action-modal" :title="`Ubah Status ${selectedResources.length} Peserta`">
            <x-form-group :horizontal="false" label="Status Kelulusan">
                <x-input-graduation-status v-model="bulkActionFields.graduation_status" />
            </x-form-group>
            <template #footer>
                <x-close-modal-button />
                <button class="btn btn-primary" @click="handleBulkGraduationStatusUpdate">Ubah</button>
            </template>
        </x-modal>
        <x-submit-button v-if="!isGraduated" :isLoading="isLoading" @click="submit" label="Finalisasi Kelulusan" class="btn btn-success" />
        <x-submit-button v-if="!isGraduated" :isLoading="isLoading" @click="saveAsDraft" label="Simpan Draft" icon-class="fa-archive" />
    </x-page>
</template>

<script>
import XAjaxDropdown from "../../components/XAjaxDropdown.vue";
import XBackButton from "../../components/XBackButton.vue";
import XCreateButton from "../../components/XCreateButton.vue";
import XQuickDeleteButton from "../../components/XQuickDeleteButton.vue";
import XQuickEditButton from "../../components/XQuickEditButton.vue";
import XFilterBox from "../../components/XFilterBox.vue";
import XFormGroup from "../../components/XFormGroup.vue";
import XPage from "../../components/XPage.vue";
import XTable from "../../components/XTable.vue";
import { handleGeneralXhrError, handleValidationXhrError, warningPrompt } from "../../helpers";
import XPagination from "../../components/XPagination.vue";
import XLabel from "../../components/XLabel.vue";
import MultiCheckMixin from "../../mixins/MultiCheckMixin";
import XModal from "../../components/XModal.vue";
import XCloseModalButton from "../../components/XCloseModalButton.vue";
import XInputGraduationStatus from "../../components/XInputGraduationStatus.vue";
import XSubmitButton from "../../components/XSubmitButton.vue";

export default {
    components: {
        XCreateButton,
        XTable,
        XQuickEditButton,
        XQuickDeleteButton,
        XPage,
        XFormGroup,
        XFilterBox,
        XAjaxDropdown,
        XBackButton,
        XPagination,
        XLabel,
        XModal,
        XCloseModalButton,
        XInputGraduationStatus,
        XSubmitButton,
    },
    computed: {
        allResourcesSelected() {
            return (
                this.displayedResources.length > 0 && this.selectedResources.length === this.displayedResources.length
            );
        },
        title() {
            if (this.form) {
                if (this.isGraduated) return `Riwayat Kelulusan ${this.form.label}`;
                return `Kelulusan ${this.form.label}`;
            }
            return null;
        },
        isGraduated() {
            return this.form && this.form.graduation_date != null;
        },
        displayedResources() {

            let list = this.resources.slice();
            const sq = this.searchQuery.toLowerCase().trim();
            if (sq) {
                list = list.filter(r => {
                    const nameMatch = r.name && r.name.toLowerCase().includes(sq);
                    const emailMatch = r.email && r.email.toLowerCase().includes(sq);
                    const codeMatch = r.code && r.code.toLowerCase().includes(sq);
                    return nameMatch || emailMatch || codeMatch;
                });
            }
            const min =
                this.attendanceFilterMin !== null && this.attendanceFilterMin !== ""
                    ? Number(this.attendanceFilterMin)
                    : null;
            const max =
                this.attendanceFilterMax !== null && this.attendanceFilterMax !== ""
                    ? Number(this.attendanceFilterMax)
                    : null;
            if (min !== null) {
                list = list.filter((r) => Number(r.attendance_percent) >= min);
            }
            if (max !== null) {
                list = list.filter((r) => Number(r.attendance_percent) <= max);
            }
            if (this.sortKey) {
                list.sort((a, b) => {
                    let valA, valB;

                    if (this.sortKey === 'attendance_percent') {
                        valA = Number(a.attendance_percent || 0);
                        valB = Number(b.attendance_percent || 0);
                        return (valA - valB) * this.sortOrder;
                    }

                    if (this.sortKey === 'email') {
                        valA = (a.member_account && a.member_account.email) ? "" : (a.email || "");
                        valB = (b.member_account && b.member_account.email) ? "" : (b.email || "");
                    } else {
                        valA = a[this.sortKey];
                        valB = b[this.sortKey];
                    }

                    valA = (valA || "").toString().toLowerCase();
                    valB = (valB || "").toString().toLowerCase();

                    if (valA === "" && valB !== "") return 1 * this.sortOrder;
                    if (valA !== "" && valB === "") return -1 * this.sortOrder;

                    if (valA < valB) return -1 * this.sortOrder;
                    if (valA > valB) return 1 * this.sortOrder;
                    return 0;
                });
            }

            return list;
        },
    },
    created() {
        this.fetchForm();
        this.fetchResources();
    },
    data() {
        return {
            attendanceFilterMin: null,
            attendanceFilterMax: null,
            searchQuery: "",
            sortKey: "attendance_percent",
            sortOrder: -1,
            bulkActionFields: {
                graduation_status: "Tidak Lulus",
            },
            form: null,
            isLoading: false,
            resources: [],
            selectedResource: null,
            selectedResources: [],
        };
    },
    methods: {
        fetchForm(route = this.$route) {
            this.isLoading = true;
            const request = axios.get(`/api/event-forms/${route.params.id}`).then((response) => {
                this.form = response.data.data;
            });
            return handleGeneralXhrError(request).finally(() => {
                this.isLoading = false;
            });
        },
        fetchResources(route = this.$route) {
            this.isLoading = true;
            const request = axios
                .get(`/api/event-forms/${route.params.id}/attendances`, {
                    params: {
                        with: ["member_account"],
                    },
                })
                .then((response) => {
                    this.resources = response.data.data.map((resource) => ({
                        ...resource,
                        graduation_status: resource.last_status ?? "Tidak Lulus",
                    }));
                });
            return handleGeneralXhrError(request).finally(() => {
                this.isLoading = false;
            });
        },
        handleBackButtonClick(router) {
            router.back();
        },
        handleBulkGraduationStatusUpdate() {
            for (const selectedResource of this.selectedResources) {
                const i = this.resources.findIndex((resource) => resource.id === selectedResource.id);
                if (i === -1) {
                    console.error(`Resource with ID ${selectedResource.id} not found in resources.`);
                    continue;
                }
                this.$set(this.resources[i], "graduation_status", this.bulkActionFields.graduation_status);
            }
            $("#bulk-action-modal").modal("hide");
            this.selectedResources = [];
        },
        isResourceSelected(resource) {
            return !!this.selectedResources.find((selectedResource) => selectedResource.id === resource.id);
        },
        showBulkActionModal() {
            $("#bulk-action-modal").modal("show");
        },
        showSubscriberDetailModal(resource) {
            this.selectedResource = { ...resource };
            $("#subscriber-detail-modal").modal("show");
        },
        submit() {
            return warningPrompt({
                title: "Simpan Data Kelulusan?",
                text: "Sekali disimpan, data kelulusan tidak dapat diubah lagi.",
                onConfirm: () => {
                    this.isLoading = true;
                    const request = axios
                        .post(`/api/event-forms/${this.$route.params.id}/graduations`, {
                            subscribers: this.resources,
                        })
                        .then((response) => {
                            this.$notify({
                                type: "success",
                                title: `Berhasil`,
                                text: `Kelulusan ${this.form.label} disimpan!`,
                            });
                            window.location = document.referrer || "/";
                        });
                    return handleValidationXhrError(request).finally(() => {
                        this.isLoading = false;
                    });
                },
            });
        },
        saveAsDraft() {
            return warningPrompt({
                title: "Simpan Sebagai Draft?",
                text: "Data kelulusan akan tersimpan sebagai draf.",
                onConfirm: () => {
                    this.isLoading = true;
                    const request = axios
                        .post(`/api/event-forms/${this.$route.params.id}/graduations/draft`, {
                            subscribers: this.resources,
                        })
                        .then((response) => {
                            this.$notify({
                                type: "success",
                                title: `Berhasil`,
                                text: `Kelulusan ${this.form.label} disimpan sebagai draf!`,
                            });
                            window.location = document.referrer || "/";
                        });
                    return handleValidationXhrError(request).finally(() => {
                        this.isLoading = false;
                    });
                },
            });
        },
        toggleAllResources() {
            if (this.allResourcesSelected) {
                // Deselect only the displayed resources
                const displayedIds = this.displayedResources.map((r) => r.id);
                this.selectedResources = this.selectedResources.filter((r) => !displayedIds.includes(r.id));
                return;
            }
            // Select only displayed resources
            const toAdd = this.displayedResources.filter((r) => !this.selectedResources.find((s) => s.id === r.id));
            this.selectedResources = this.selectedResources.concat(toAdd);
        },
        toggleMultiResourceSelect(checkbox) {
            const resourceId = checkbox.dataset.resourceId;
            const resource = this.resources.find((res) => res.id === parseInt(resourceId));

            if (!resource) return;

            this.toggleResourceSelect(resource);
        },
        toggleResourceSelect(resource) {
            if (this.isResourceSelected(resource)) {
                this.selectedResources = this.selectedResources.filter(
                    (selectedResource) => selectedResource.id !== resource.id
                );
                return;
            }
            this.selectedResources.push(resource);
        },
        clearAttendanceFilter() {
            this.attendanceFilterMin = null;
            this.attendanceFilterMax = null;
            this.searchQuery = "";
            const displayedIds = this.displayedResources.map((r) => r.id);
            this.selectedResources = this.selectedResources.filter((r) => !displayedIds.includes(r.id));
        },
        toggleSort(key) {
            if (this.sortKey === key) {
                this.sortOrder = this.sortOrder === 1 ? -1 : 1;
            } else {
                this.sortKey = key;
                this.sortOrder = key === 'attendance_percent' ? -1 : 1;
            }
        },
    },
    mixins: [MultiCheckMixin],
};
</script>

<style lang="scss" scoped>
.attendance-filter-container {
    display: flex;
    flex-direction: column;
    justify-content: flex-end;
    margin-bottom: 1rem;
    max-width: 100%;

    @media (min-width: 786px) {
        margin-bottom: 0;
    }
}
.attendance-filter-inner-container {
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
    max-width: 100%;

    @media (min-width: 768px) {
        flex-direction: row;
    }
}
.attendance-filter-input {
    width: 100%;
}
</style>
